import { Button, Popconfirm, Tag } from 'antd';
import { FileText, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../components/EmptyState';
import type { DocumentType } from '../reference-data/reference-data.api';
import { COMPLIANCE_LABELS } from './compliance-labels';
import { TruckDocumentModal } from './TruckDocumentModal';
import type { TruckDocumentView } from './trucks.api';
import type { DocumentFormInput } from './use-truck-documents';

interface TruckDocumentsProps {
  documents: TruckDocumentView[];
  documentTypes: DocumentType[];
  canManage: boolean;
  submitting: boolean;
  onAdd: (input: DocumentFormInput & { file: File }) => Promise<boolean>;
  onEdit: (documentId: string, input: DocumentFormInput) => Promise<boolean>;
  onRemove: (documentId: string) => Promise<void>;
  onOpen: (documentId: string) => Promise<void>;
}

/** A truck's compliance documents — attach/edit/remove are ordinary edits (CLAUDE.md's append-only rule is for the odometer, not this). */
export function TruckDocuments({ documents, documentTypes, canManage, submitting, onAdd, onEdit, onRemove, onOpen }: TruckDocumentsProps) {
  const [editing, setEditing] = useState<TruckDocumentView | 'new' | null>(null);

  async function submit(input: DocumentFormInput): Promise<boolean> {
    if (editing === 'new') return input.file ? onAdd({ ...input, file: input.file }) : false;
    if (editing) return onEdit(editing.id, input);
    return false;
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-fg">Documents</h2>
        {canManage && (
          <Button size="small" icon={<Plus className="size-3.5" />} onClick={() => setEditing('new')}>
            Add document
          </Button>
        )}
      </div>
      {documents.length === 0 && (
        <EmptyState icon={FileText} title="No documents yet" description="Attach this truck's compliance documents as they're issued." />
      )}
      {documents.length > 0 && (
        <ul className="flex flex-col gap-3">
          {documents.map((document) => (
            <DocumentRow key={document.id} document={document} canManage={canManage} onEdit={() => setEditing(document)} onRemove={onRemove} onOpen={onOpen} />
          ))}
        </ul>
      )}
      <TruckDocumentModal
        key={editing === 'new' ? 'new' : (editing?.id ?? 'closed')}
        open={editing !== null}
        document={editing === 'new' ? null : editing}
        documentTypes={documentTypes}
        submitting={submitting}
        onSubmit={async (input) => {
          const saved = await submit(input);
          if (saved) setEditing(null);
          return saved;
        }}
        onCancel={() => setEditing(null)}
      />
    </section>
  );
}

function DocumentRow({
  document,
  canManage,
  onEdit,
  onRemove,
  onOpen,
}: {
  document: TruckDocumentView;
  canManage: boolean;
  onEdit: () => void;
  onRemove: (documentId: string) => Promise<void>;
  onOpen: (documentId: string) => Promise<void>;
}) {
  const compliance = COMPLIANCE_LABELS[document.complianceStatus];
  return (
    <li className="flex items-center justify-between gap-3 rounded-lg border border-line bg-surface px-4 py-3">
      <div>
        <button type="button" className="font-medium text-link hover:underline" onClick={() => onOpen(document.id)}>
          {document.documentTypeName}
        </button>
        <p className="text-sm text-muted">Expires {new Date(document.expiryDate).toLocaleDateString()}</p>
      </div>
      <div className="flex items-center gap-3">
        <Tag color={compliance.color}>{compliance.label}</Tag>
        {canManage && (
          <div className="flex items-center gap-1">
            <Button type="text" size="small" icon={<Pencil className="size-3.5" />} onClick={onEdit} aria-label={`Edit ${document.documentTypeName}`} />
            <Popconfirm title={`Remove ${document.documentTypeName}?`} onConfirm={() => onRemove(document.id)}>
              <Button type="text" size="small" danger icon={<Trash2 className="size-3.5" />} aria-label={`Remove ${document.documentTypeName}`} />
            </Popconfirm>
          </div>
        )}
      </div>
    </li>
  );
}
